/**
 * The two shapes of navigation, and nothing else.
 *
 * Neither of these decides which one is shown — `App.tsx` does, from `useLayout()`. They are handed
 * a screen and a callback, which is what keeps them layout-agnostic: nothing here reads the window
 * width, per the design system's rule that a component needing to know how wide the screen is is
 * two components, or is its parent's problem.
 */
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import type { Screen } from '@/store';
import * as React from 'react';
import { Pressable, ScrollView, View } from 'react-native';

interface NavItem {
  id: Screen;
  label: string;
  glyph: string;
}

/** Order matters: this is the order both shells render, and Upcoming is the landing screen. */
export const NAV_ITEMS: NavItem[] = [
  { id: 'upcoming', label: 'Upcoming', glyph: '▦' },
  { id: 'predictions', label: 'Predicted', glyph: '◈' },
  { id: 'bets', label: 'Bets', glyph: '☰' },
  { id: 'live', label: 'Live', glyph: '◉' },
  { id: 'complete', label: 'Complete', glyph: '✓' },
  { id: 'leagues', label: 'Leagues', glyph: '⚑' },
  { id: 'backtest', label: 'Backtest', glyph: '⟲' },
  { id: 'settings', label: 'Settings', glyph: '⚙' },
];

interface NavProps {
  screen: Screen;
  onGo: (screen: Screen) => void;
}

interface ThemeToggleProps {
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
}

/** The wide shell's side rail — vertical, so eight entries read as a list. */
export function SideRail({ screen, onGo, theme, onToggleTheme, badge }: NavProps & ThemeToggleProps & { badge?: Partial<Record<Screen, number>> }) {
  return (
    <View className="border-border bg-card w-52 shrink-0 border-r">
      <View className="gap-1 px-3 py-5">
        <Text variant="title" className="px-2 pb-3">
          PolySoccerBets
        </Text>

        {NAV_ITEMS.map((item) => {
          const active = item.id === screen;
          const count = badge?.[item.id];
          return (
            <Pressable
              key={item.id}
              onPress={() => onGo(item.id)}
              role="tab"
              aria-selected={active}
              className={cn(
                'flex-row items-center gap-3 rounded-md px-3 py-2',
                active ? 'bg-accent' : 'active:bg-muted web:hover:bg-muted',
              )}>
              <Text
                className={cn(
                  'w-5 text-center',
                  active ? 'text-accent-foreground' : 'text-muted-foreground',
                )}>
                {item.glyph}
              </Text>
              <Text
                className={cn(
                  'flex-1',
                  active ? 'text-accent-foreground font-medium' : 'text-muted-foreground',
                )}>
                {item.label}
              </Text>
              {count ? (
                <Text variant="caption" className="text-muted-foreground">
                  {count}
                </Text>
              ) : null}
            </Pressable>
          );
        })}
      </View>

      <View className="mt-auto px-3 pb-5">
        <ThemeToggle theme={theme} onToggleTheme={onToggleTheme} />
      </View>
    </View>
  );
}

/**
 * The narrow shell's bottom bar.
 *
 * It scrolls sideways, deliberately. Eight labels squeezed into a phone's width are eight targets
 * too small to hit, and this is the first control anyone touches, one-handed. The active tab is
 * scrolled towards the centre, because a tap that lands half off the edge reads as a tap on
 * something else.
 */
export function BottomNav({ screen, onGo }: NavProps) {
  const ref = React.useRef<ScrollView>(null);
  const index = NAV_ITEMS.findIndex((s) => s.id === screen);

  React.useEffect(() => {
    if (index < 0) return;
    // One item's width times its position, less half a viewport — near enough to centred without
    // measuring, and it degrades to "scrolled to the start" for the first few tabs.
    ref.current?.scrollTo({ x: Math.max(0, index * ITEM_WIDTH - 120), animated: true });
  }, [index]);

  return (
    <View className="border-border bg-card border-t">
      <ScrollView
        ref={ref}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerClassName="px-2 py-1">
        {NAV_ITEMS.map((item) => {
          const active = item.id === screen;
          return (
            <Pressable
              key={item.id}
              onPress={() => onGo(item.id)}
              role="tab"
              aria-selected={active}
              style={{ width: ITEM_WIDTH }}
              className="items-center gap-0.5 rounded-md py-2">
              <Text className={cn(active ? 'text-primary' : 'text-muted-foreground')}>
                {item.glyph}
              </Text>
              <Text
                variant="caption"
                className={cn(active ? 'text-primary' : 'text-muted-foreground')}>
                {item.label}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

/** Fixed so the scroll-to-centre above can be arithmetic rather than a measurement pass. */
const ITEM_WIDTH = 76;

export function ThemeToggle({ theme, onToggleTheme }: ThemeToggleProps) {
  return (
    <Pressable
      onPress={onToggleTheme}
      role="button"
      accessibilityLabel={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      className="active:bg-muted web:hover:bg-muted flex-row items-center gap-2 rounded-md px-3 py-2">
      <Text className="text-muted-foreground w-5 text-center">{theme === 'dark' ? '☾' : '☀'}</Text>
      <Text variant="small" className="text-muted-foreground">
        {theme === 'dark' ? 'Dark' : 'Light'}
      </Text>
    </Pressable>
  );
}

/** The narrow shell's top strip: the app name, and the mode toggle the rail carries when wide. */
export function NarrowHeader({ theme, onToggleTheme }: ThemeToggleProps) {
  return (
    <View className="flex-row items-center justify-between">
      <Text variant="title">PolySoccerBets</Text>
      <ThemeToggle theme={theme} onToggleTheme={onToggleTheme} />
    </View>
  );
}
